/**
 * 安全配置组件
 *
 * 管理自动封禁阈值、云端审核开关等安全设置。
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  safeguardAdminApi,
  type SafeguardSettings,
} from '../../services/safeguardAdminApi';

export const SafeguardSettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<SafeguardSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      const result = await safeguardAdminApi.getSettings();
      setSettings(result);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取配置失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await safeguardAdminApi.updateSettings(settings);
      setSuccess('设置已保存');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm">{success}</div>
      )}

      {/* 自动封禁配置 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4">自动封禁配置</h3>
        <div className="space-y-4">
          <ToggleRow
            label="启用自动封禁"
            description="连续高风险违规达到阈值后自动封禁用户"
            checked={settings.auto_ban_enabled}
            onChange={v => setSettings({ ...settings, auto_ban_enabled: v })}
          />
          <NumberRow
            label="封禁阈值"
            description="连续高风险违规次数达到此值触发自动封禁"
            value={settings.auto_ban_threshold}
            min={1}
            max={20}
            onChange={v => setSettings({ ...settings, auto_ban_threshold: v })}
          />
          <NumberRow
            label="自动封禁时长（小时）"
            description="自动封禁的持续时间"
            value={settings.auto_ban_duration_hours}
            min={1}
            max={8760}
            onChange={v => setSettings({ ...settings, auto_ban_duration_hours: v })}
          />
          <NumberRow
            label="违规计数重置周期（天）"
            description="超过此时间无违规则违规计数清零"
            value={settings.violation_reset_days}
            min={1}
            max={365}
            onChange={v => setSettings({ ...settings, violation_reset_days: v })}
          />
        </div>
      </div>

      {/* 审核配置 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4">审核配置</h3>
        <div className="space-y-4">
          <ToggleRow
            label="启用本地敏感词审核"
            description="使用AC自动机快速匹配敏感词"
            checked={settings.local_enabled}
            disabled
            onChange={() => {}}
          />
          <ToggleRow
            label="启用云端审核（阿里云AI护栏）"
            description="边缘通过时触发阿里云深度审核"
            checked={settings.cloud_enabled}
            disabled
            onChange={() => {}}
          />
          <ToggleRow
            label="记录审核日志"
            description="记录所有审核检查的详细结果"
            checked={settings.audit_log_enabled}
            disabled
            onChange={() => {}}
          />
          <ToggleRow
            label="记录完整触发内容"
            description="在审核日志中记录触发违规的完整文本内容"
            checked={settings.trigger_content_enabled}
            onChange={v => setSettings({ ...settings, trigger_content_enabled: v })}
          />
          <NumberRow
            label="日志保留天数"
            description="超过此天数的审核日志将被自动清理"
            value={settings.log_retention_days}
            min={7}
            max={365}
            disabled
            onChange={() => {}}
          />
        </div>
      </div>

      {/* 保存按钮 */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
        >
          {saving ? '保存中...' : '保存设置'}
        </button>
      </div>
    </div>
  );
};

const ToggleRow: React.FC<{
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}> = ({ label, description, checked, disabled, onChange }) => (
  <div className="flex items-center justify-between">
    <div>
      <div className="text-sm text-gray-700">{label}</div>
      <div className="text-xs text-gray-400">{description}</div>
    </div>
    <button
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
      } ${checked ? 'bg-blue-500' : 'bg-gray-300'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`} />
    </button>
  </div>
);

const NumberRow: React.FC<{
  label: string;
  description: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}> = ({ label, description, value, min, max, disabled, onChange }) => (
  <div className="flex items-center justify-between">
    <div>
      <div className="text-sm text-gray-700">{label}</div>
      <div className="text-xs text-gray-400">{description}</div>
    </div>
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      disabled={disabled}
      onChange={e => onChange(Math.max(min, Math.min(max, parseInt(e.target.value, 10) || min)))}
      className={`border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-24 text-right ${
        disabled ? 'bg-gray-50 text-gray-400' : ''
      }`}
    />
  </div>
);
